// Protocol bookmarks — localStorage-first with optional server sync.
//
// Non-logged-in users get localStorage-only bookmarks (graceful degradation).
// Logged-in users (a `pp_auth` token is present) also persist to the backend
// via /api/users/me/bookmarks so bookmarks follow them across devices.
//
// localStorage key shape: pp_bookmarked_protocols = JSON array of protocol IDs.
import axios from "axios";
import { getAuthHeaders, isAuthenticated } from "./authHeaders";

const STORAGE_KEY = "pp_bookmarked_protocols";
const API = `${""}/api`;

export function getBookmarks() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function saveBookmarks(ids) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // localStorage may be disabled / full — fail open, server stays source of truth.
  }
}

export function isBookmarked(id) {
  return getBookmarks().includes(id);
}

/**
 * Toggle a protocol's bookmark state. Updates localStorage immediately and,
 * when logged in, mirrors the change to the backend (fire-and-forget — UI does
 * not block on the network). Returns the new array of bookmarked IDs.
 */
export function toggleBookmark(id) {
  const current = getBookmarks();
  const exists = current.includes(id);
  const next = exists ? current.filter((x) => x !== id) : [...current, id];
  saveBookmarks(next);

  if (isAuthenticated()) {
    const headers = getAuthHeaders();
    if (exists) {
      axios.delete(`${API}/users/me/bookmarks/${encodeURIComponent(id)}`, { headers }).catch(() => {});
    } else {
      axios.post(`${API}/users/me/bookmarks`, { protocol_id: id }, { headers }).catch(() => {});
    }
  }
  return next;
}

/**
 * On startup (and on auth-state change), pull server-side bookmarks for the
 * logged-in user and adopt them as the source of truth for cross-device sync.
 *
 * REGRESSION NOTE (server-is-source-of-truth): we OVERWRITE localStorage with
 * the server response rather than union-merging. The server is the durable,
 * cross-device record, so a fresh login on any device should reflect exactly
 * what the account has saved — not whatever stale IDs happen to be cached in
 * that browser's localStorage. The toggleBookmark() path keeps the two in step
 * afterward by mirroring every add/remove to the backend. No-op for anonymous
 * users (their localStorage-only bookmarks are left untouched).
 * Returns the resulting array.
 */
export async function syncBookmarksFromServer() {
  if (!isAuthenticated()) return getBookmarks();
  try {
    const res = await axios.get(`${API}/users/me/bookmarks`, { headers: getAuthHeaders() });
    const serverIds = Array.isArray(res.data?.bookmarks) ? res.data.bookmarks : [];
    saveBookmarks(serverIds);
    return serverIds;
  } catch {
    return getBookmarks();
  }
}
